import { useEffect, useState } from "react";
import Modal from "../../Modal";
import { FormattedMessage, useIntl } from "react-intl";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { useSelector } from "react-redux";
import useCountry from "../../../hooks/useCountry";
import useProfile from "../../../hooks/useProfile";
import { currentEnv } from "../../../utils/utils";
import ReviewDetailsStep from "./Steps/ReviewDetailsStep";
import SelectLinkDetailsStep from "./Steps/SelectLinkDetailsStep";
import GeneratedLinkStep from "./Steps/GeneratedLinkStep";
import "./styles.css";
const CocoLinkGeneratorModal = ({
  isCocoLinkGeneratorOpen,
  setIsCocoLinkGeneratorOpen,
  user,
}) => {
  const intl = useIntl();

  const { countryLanguages } = useSelector((state) => state.controller);
  const { country, getCountry } = useCountry();
  const { tokenData } = useProfile();
  const environment = currentEnv;
  const [step, setStep] = useState(1);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const {
    salutation,
    title,
    first_name,
    last_name,
    controllerCode,
    customerScope,
    language,
    sub_identities,
    aliases,
    sourceSystem,
    value,
    contact_email,
  } = user;

  // Normalize identity types: vault and vault_id are the same
  const normalizeType = (type) => {
    if (!type) return "";
    const lower = type.toLowerCase();
    return lower === "vault" ? "vault_id" : lower;
  };

  const knownSourceSystems = ["veeva", "vault", "balios"];

  // Combine all identities (sub_identities + aliases + main identity)
  const combinedIdentities = [
    ...(sub_identities || []),
    ...(aliases || []),
    ...(sourceSystem && value ? [{ type: sourceSystem, value }] : []),
  ];

  // Group identities by normalized type and remove duplicates
  let identitiesByType = {};
  combinedIdentities.forEach((item) => {
    if (!item?.type || !item?.value) return;
    const normalizedType = normalizeType(item.type);
    if (!identitiesByType[normalizedType]) {
      identitiesByType[normalizedType] = [];
    }
    // Add only if not already present
    if (!identitiesByType[normalizedType].some((x) => x.value === item.value)) {
      identitiesByType[normalizedType].push(item);
    }
  });
  if (contact_email) {
    identitiesByType.email = identitiesByType.email || [];
    identitiesByType.email.push({ type: "email", value: contact_email });
  }
  const uniqueData = Object.fromEntries(
    Object.entries(identitiesByType).map(([key, arr]) => [
      key,
      [...new Map(arr.map((item) => [item.value, item])).values()],
    ]),
  );
  identitiesByType = uniqueData;


  // Get available source systems from combined identities
  const availableSourceSystems = Object.keys(identitiesByType).filter((type) =>
    knownSourceSystems.includes(normalizeType(type)),
  );

  // Set default source system: prefer current, then first available, fallback to "veeva"
  const defaultSourceSystem =
    availableSourceSystems.includes(normalizeType(sourceSystem)) &&
    normalizeType(sourceSystem)
      ? normalizeType(sourceSystem)
      : availableSourceSystems[0] || "veeva";

  // Get customer ID options for selected source system
  const getCustomerOptions = (sourceSys) => {
    const normalizedSys = normalizeType(sourceSys);
    return identitiesByType[normalizedSys] || [];
  };

  // Get email options
  const emailOptions = identitiesByType.email || [];
  const defaultEmailOne = emailOptions[0]?.value || "";
  const defaultEmailTwo = emailOptions[1]?.value || "";
  const defaultCustomerId =
    getCustomerOptions(defaultSourceSystem)[0]?.value || "";
  const [linkData, setLinkData] = useState({
    emailOne: defaultEmailOne,
    emailTwo: defaultEmailTwo,
    sourceSystem: defaultSourceSystem,
    customerId: defaultCustomerId,
    cocoLinktype: "cocoonline",
  });

  useEffect(() => {
    setLinkData((prev) => ({
      ...prev,
      sourceSystem: defaultSourceSystem,
      customerId: defaultCustomerId,
    }));
  }, [defaultSourceSystem, defaultCustomerId]);

  useEffect(() => {
    if (controllerCode && customerScope)
      getCountry(controllerCode, customerScope);
  }, [controllerCode, customerScope]);
  const handleCloseAndCancel = async () => {
    setIsCocoLinkGeneratorOpen(false);
    setStep(1);
  };
  const handleNext = () => {
    setStep((prevStep) => prevStep + 1);
  };
  const handlePrev = () => {
    setStep((prevStep) => prevStep - 1);
  };
  return (
    <Modal
      title={intl.formatMessage({
        id: "id.details.generate-Coco-Link",
        defaultMessage: "Generate Coco Link",
      })}
      open={isCocoLinkGeneratorOpen}
      setOpen={setIsCocoLinkGeneratorOpen}
      okText={intl.formatMessage({
        id: "label.save",
        defaultMessage: "Save",
      })}
      canelText={intl.formatMessage({
        id: "label.cancel",
        defaultMessage: "Cancel",
      })}
      onSubmit={() => {}}
      onCancel={handleCloseAndCancel}
      handleClose={handleCloseAndCancel}
      noActions={true}
      customAction={true}
      type="coco-link-generator"
    >
      <div className="coco-link-generator-content">
        {step === 1 && (
          <ReviewDetailsStep
            controllerCode={controllerCode}
            language={language}
            countryLanguages={countryLanguages}
            country={country}
            tokenData={tokenData}
            salutation={salutation}
            title={title}
            first_name={first_name}
            last_name={last_name}
            environment={environment}
          />
        )}

        {step === 2 && (
          <SelectLinkDetailsStep
            identitiesByType={identitiesByType}
            emailOptions={emailOptions}
            availableSourceSystems={availableSourceSystems}
            linkData={linkData}
            setLinkData={setLinkData}
            hasSubmitted={hasSubmitted}
          />
        )}
        {step === 3 && (
          <GeneratedLinkStep
            linkData={linkData}
            user={user}
            country={country}
            tokenData={tokenData}
            environment={environment}
          />
        )}
      </div>
      <div className="coco-link-generator-footer">
        <button
          className="btn-primary btn-secondary px-3 d-flex justify-content-center align-items-center custom-theme-color"
          onClick={handlePrev}
          disabled={step === 1}
        >
          <LuChevronLeft size={20} />
          <FormattedMessage id="id.generate-prev" defaultMessage="Previous" />
        </button>
        {step !== 2 && step !== 3 && (
          <button
            className="btn-primary px-3 d-flex justify-content-center align-items-center"
            onClick={handleNext}
            disabled={step === 2}
          >
            <FormattedMessage id="id.generate-next" defaultMessage="Next" />
            <LuChevronRight size={20} />
          </button>
        )}

        {step === 2 && (
          <button
            className="btn-primary px-3"
            onClick={() => {
              if (
                linkData.sourceSystem &&
                linkData.customerId &&
                linkData.cocoLinktype
              ) {
                setStep(3);
                setHasSubmitted(false);
              } else {
                setHasSubmitted(true);
              }
            }}
          >
            <FormattedMessage
              id="id.generate-link"
              defaultMessage="Generate Link"
            />
          </button>
        )}
      </div>
    </Modal>
  );
};

export default CocoLinkGeneratorModal;
